// src/pages/delivery/DriverSummary.jsx
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';

const GREEN = '#2e9f86';
const ACCENT_AMBER = '#f59e0b';

const pad = (x) => String(x).padStart(2, '0');
const fmtLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayLocal = () => fmtLocal(new Date());
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const asArray = (d) =>
  Array.isArray(d) ? d
  : Array.isArray(d?.data) ? d.data
  : Array.isArray(d?.items) ? d.items
  : [];

const STATUS_STYLE = {
  pending:    { bg: '#fef3c7', color: '#92400e' },
  dispatched: { bg: '#dbeafe', color: '#1e40af' },
  delivered:  { bg: '#d1fae5', color: '#065f46' },
  failed:     { bg: '#fee2e2', color: '#991b1b' },
};

export default function DriverSummary() {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayLocal());
  const [selectedDriverId, setSelectedDriverId] = useState('all');

  // fetch all delivery sales for the chosen date
  const { data: rawDocs = [], isLoading: loadingDocs } = useQuery(
    ['driver-summary-docs', date],
    () => api.get(`/api/documents?type=200&status=confirmed&date_from=${date}&date_to=${date}`)
          .then((r) => asArray(r.data)),
    { keepPreviousData: true }
  );

  // fetch all users with role=driver
  const { data: rawUsers = [], isLoading: loadingUsers } = useQuery(
    ['users-drivers'],
    () => api.get('/api/users').then((r) => asArray(r.data))
  );

  const drivers = useMemo(() => rawUsers.filter((u) => u.role === 'driver'), [rawUsers]);

  // only delivery-enabled docs
  const deliveryDocs = useMemo(
    () => rawDocs.filter((d) => d.delivery?.enabled),
    [rawDocs]
  );

  // group by driver
  const grouped = useMemo(() => {
    const map = {};
    // unassigned bucket
    map['unassigned'] = { driver: { _id: 'unassigned', name: 'Unassigned' }, docs: [] };
    drivers.forEach((dr) => { map[dr._id] = { driver: dr, docs: [] }; });

    deliveryDocs.forEach((doc) => {
      const dId = doc.delivery?.driver_id ? String(doc.delivery.driver_id) : 'unassigned';
      if (!map[dId]) map[dId] = { driver: { _id: dId, name: 'Unknown Driver' }, docs: [] };
      map[dId].docs.push(doc);
    });

    return Object.values(map).filter((g) => g.docs.length > 0);
  }, [deliveryDocs, drivers]);

  const filteredGroups = useMemo(() =>
    selectedDriverId === 'all' ? grouped : grouped.filter((g) => String(g.driver._id) === selectedDriverId),
    [grouped, selectedDriverId]
  );

  const loading = loadingDocs || loadingUsers;

  // grand totals
  const grandTotals = useMemo(() => {
    const all = filteredGroups.flatMap((g) => g.docs);
    return {
      count:     all.length,
      total:     all.reduce((s, d) => s + Number(d.totals?.total || 0), 0),
      collected: all.reduce((s, d) => s + Number(d.totals?.paid  || 0), 0),
      balance:   all.reduce((s, d) => s + Number(d.totals?.balance || 0), 0),
    };
  }, [filteredGroups]);

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: 900, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111', margin: 0 }}>Driver Summary</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Driver filter */}
          <select
            value={selectedDriverId}
            onChange={(e) => setSelectedDriverId(e.target.value)}
            style={{ border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '7px 12px', fontSize: 13, background: '#fff', color: '#111', outline: 'none' }}
          >
            <option value="all">All Drivers</option>
            {drivers.map((dr) => (
              <option key={dr._id} value={dr._id}>{dr.name}</option>
            ))}
          </select>
          {/* Date picker */}
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '7px 12px', fontSize: 13, background: '#fff', color: '#111', outline: 'none' }}
          />
        </div>
      </div>

      {/* Grand totals bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Total Orders',  value: grandTotals.count,     fmt: (v) => v,         color: '#3b82f6' },
          { label: 'Total Value',   value: grandTotals.total,     fmt: money,             color: GREEN },
          { label: 'Collected',     value: grandTotals.collected, fmt: money,             color: GREEN },
          { label: 'Outstanding',   value: grandTotals.balance,   fmt: money,             color: grandTotals.balance > 0 ? '#ef4444' : GREEN },
        ].map((c) => (
          <div key={c.label} style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: '12px 16px', borderLeft: `3px solid ${c.color}` }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#111' }}>{loading ? '…' : c.fmt(c.value)}</div>
          </div>
        ))}
      </div>

      {/* Loading / empty */}
      {loading && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 48, fontSize: 14 }}>Loading…</div>}
      {!loading && filteredGroups.length === 0 && (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: 48, fontSize: 14, background: '#fff', borderRadius: 12, border: '1.5px solid #f0f0f0' }}>
          No delivery orders found for this date.
        </div>
      )}

      {/* Per-driver cards */}
      {!loading && filteredGroups.map(({ driver, docs }) => {
        const dTotal     = docs.reduce((s, d) => s + Number(d.totals?.total   || 0), 0);
        const dCollected = docs.reduce((s, d) => s + Number(d.totals?.paid    || 0), 0);
        const dBalance   = docs.reduce((s, d) => s + Number(d.totals?.balance || 0), 0);

        const statusCounts = docs.reduce((acc, d) => {
          const s = d.delivery?.status || 'pending';
          acc[s] = (acc[s] || 0) + 1;
          return acc;
        }, {});

        return (
          <div key={driver._id} style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>

            {/* Driver header */}
            <div style={{ padding: '14px 18px', borderBottom: '1.5px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, background: '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Avatar */}
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#065f46', flexShrink: 0 }}>
                  {driver.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{driver.name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {docs.length} order{docs.length !== 1 ? 's' : ''}
                    {Object.entries(statusCounts).map(([s, n]) => (
                      <span key={s} style={{ marginLeft: 8, padding: '1px 7px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: STATUS_STYLE[s]?.bg || '#f3f4f6', color: STATUS_STYLE[s]?.color || '#6b7280' }}>
                        {n} {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              {/* Driver totals */}
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>Value</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{money(dTotal)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>Collected</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#065f46' }}>{money(dCollected)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>Outstanding</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: dBalance > 0 ? '#dc2626' : '#065f46' }}>{money(dBalance)}</div>
                </div>
              </div>
            </div>

            {/* Orders table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  {['#', 'Address', 'Phone', 'Total', 'Collected', 'Balance', 'Status'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {docs.map((doc, idx) => {
                  const st = doc.delivery?.status || 'pending';
                  const ss = STATUS_STYLE[st] || { bg: '#f3f4f6', color: '#6b7280' };
                  return (
                    <tr
                      key={doc._id}
                      onClick={() => navigate(`/documents/${doc._id}`)}
                      style={{ borderBottom: idx < docs.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', background: idx % 2 ? '#fafafa' : '#fff' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f0fdf8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 ? '#fafafa' : '#fff'}
                    >
                      <td style={{ padding: '9px 14px', fontWeight: 600, color: '#111' }}>{doc.number || '—'}</td>
                      <td style={{ padding: '9px 14px', color: '#374151', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.delivery?.address || '—'}</td>
                      <td style={{ padding: '9px 14px', color: '#374151' }}>{doc.delivery?.phone || '—'}</td>
                      <td style={{ padding: '9px 14px', fontWeight: 600, color: '#111' }}>{money(doc.totals?.total)}</td>
                      <td style={{ padding: '9px 14px', color: '#065f46', fontWeight: 600 }}>{money(doc.totals?.paid)}</td>
                      <td style={{ padding: '9px 14px', color: Number(doc.totals?.balance) > 0 ? '#dc2626' : '#065f46', fontWeight: 600 }}>{money(doc.totals?.balance)}</td>
                      <td style={{ padding: '9px 14px' }}>
                        <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: ss.bg, color: ss.color }}>{st}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Driver footer total row */}
              <tfoot>
                <tr style={{ borderTop: '1.5px solid #e5e7eb', background: '#f8f9fa' }}>
                  <td colSpan={3} style={{ padding: '9px 14px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>TOTAL — {driver.name}</td>
                  <td style={{ padding: '9px 14px', fontWeight: 700, color: '#111' }}>{money(dTotal)}</td>
                  <td style={{ padding: '9px 14px', fontWeight: 700, color: '#065f46' }}>{money(dCollected)}</td>
                  <td style={{ padding: '9px 14px', fontWeight: 700, color: dBalance > 0 ? '#dc2626' : '#065f46' }}>{money(dBalance)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}
    </div>
  );
}
